import { create } from 'zustand';
import { ColumnSettingsStore } from './_columnSettingsTypes';
import _bicyclesColumns from '@modules/bicycles/_bicyclesColumns';
import { Agenda } from './_columnSettingsEnums';
import _delictsColumns from '@modules/delicts/_delictsColumns';
import _dogsColumns from '@modules/dogs/_dogsColumns';
import { employeesGridColumns } from '@modules/employees/_employeesColumns';
import _errorsColumns from '@modules/errors/_errorsColumns';
import { eventsGridColumns } from '@modules/events/_eventsColumns';
import _historyColumns from '@modules/history/_historyColumns';
import _dayplansColumns from '@modules/hr-dayplans/_dayplansColumns';
import _requestsHolidayColumns from '@modules/hr-requests/holidays/_requestsHolidayColumns';
import _requestsPayColumns from '@modules/hr-requests/pays/_requestsPayColumns';
import _shiftCyclesColumns from '@modules/hr-shift-cycles/_shiftCyclesColumns';
import _shiftsColumns from '@modules/hr-shifts/_shiftsColumns';
import _calendarsColumns from '@modules/hr-templates/calendars/_calendarsColumns';
import _daysColumns from '@modules/hr-templates/days/_daysColumns';
import _periodsColumns from '@modules/hr-templates/periods/_periodsColumns';
import _weeksColumns from '@modules/hr-templates/weeks/_weeksColumns';
import _workDayTypesColumns from '@modules/hr-workDayTypes/_workDayTypesColumns';
import _workPositionsColumns from '@modules/hr-workPositions/_workPositionsColumns';
import _claimsColumns from '@modules/hr-workTimes/claim/_claimsColumns';
import _inventoryCardsColumns from '@modules/inventoryCards/_inventoryCardsColumns';
import _jobsColumns, { jobRunColumns } from '@modules/jobs/_jobsColumns';
import _myItemsColumns from '@modules/myItems/_myItemsColumns';
import _operationalWarehouseColumns from '@modules/operationalWarehouse/_operationalWarehouseColumns';
import _protocolsColumns from '@modules/protocols/_protocolsColumns';
import _repairsColumns from '@modules/repairs/_repairsColumns';
import _reportsColumns from '@modules/reports/_reportsColumns';
import { myRequestsColumns, requestColumns } from '@modules/requests/_requestsColumns';
import { employeesRoleGridColumns } from '@modules/roles-assing/_rolesAssignColumns';
import _rolesCreateColumns from '@modules/roles-create/_rolesCreateColumns';
import { tasksGridColumns } from '@modules/tasks/_tasksColumns';
import _ticketsColumns from '@modules/tickets/_ticketsColumns';
import _tripsColumns from '@modules/trips/_tripsColumns';
import { wantedPersonsGridColumns } from '@modules/wantedPersons/_wantedPersonsColumns';
import { wantedVehiclesGridColumns } from '@modules/wantedVehicles/_wantedVehiclesColumns';

const addActiveState = columns => {
  return columns.map(v => ({ ...v, active: true }));
};

export const defaultColumnSettings = {
  [Agenda.BICYCLES]: addActiveState(_bicyclesColumns),
  [Agenda.DELICTS]: addActiveState(_delictsColumns),
  [Agenda.DOGS]: addActiveState(_dogsColumns),
  [Agenda.EMPLOYEES]: addActiveState(employeesGridColumns),
  [Agenda.ERRORS]: addActiveState(_errorsColumns),
  [Agenda.EVENTS]: addActiveState(eventsGridColumns),
  [Agenda.HISTORY]: addActiveState(_historyColumns),
  [Agenda.DAYPLANS]: addActiveState(_dayplansColumns),
  [Agenda.REQUESTS_HOLIDAY]: addActiveState(_requestsHolidayColumns),
  [Agenda.REQUESTS_PAY]: addActiveState(_requestsPayColumns),
  [Agenda.SHIFT_CYCLES]: addActiveState(_shiftCyclesColumns),
  [Agenda.SHIFTS]: addActiveState(_shiftsColumns),
  [Agenda.CALENDARS]: addActiveState(_calendarsColumns),
  [Agenda.DAYS]: addActiveState(_daysColumns),
  [Agenda.PERIODS]: addActiveState(_periodsColumns),
  [Agenda.WEEKS]: addActiveState(_weeksColumns),
  [Agenda.WORK_DAY_TYPES]: addActiveState(_workDayTypesColumns),
  [Agenda.WORK_POSITIONS]: addActiveState(_workPositionsColumns),
  [Agenda.CLAIMS]: addActiveState(_claimsColumns),
  [Agenda.INVENTORY_CARDS]: addActiveState(_inventoryCardsColumns),
  [Agenda.JOB_RUNS]: addActiveState(jobRunColumns),
  [Agenda.JOBS]: addActiveState(_jobsColumns),
  [Agenda.MY_ITEMS]: addActiveState(_myItemsColumns),
  [Agenda.OPERATIONAL_WAREHOUSE]: addActiveState(_operationalWarehouseColumns),
  [Agenda.PROTOCOLS]: addActiveState(_protocolsColumns),
  [Agenda.REPAIRS]: addActiveState(_repairsColumns),
  [Agenda.REPORTS]: addActiveState(_reportsColumns),
  [Agenda.REQUESTS]: addActiveState(requestColumns),
  [Agenda.MY_REQUESTS]: addActiveState(myRequestsColumns),
  [Agenda.ROLES_ASSIGN]: addActiveState(employeesRoleGridColumns),
  [Agenda.ROLES_CREATE]: addActiveState(_rolesCreateColumns),
  [Agenda.MY_TASKS]: addActiveState(tasksGridColumns),
  [Agenda.TASKS]: addActiveState(tasksGridColumns),
  [Agenda.TICKETS]: addActiveState(_ticketsColumns),
  [Agenda.TRIPS]: addActiveState(_tripsColumns),
  [Agenda.WANTED_PERSONS]: addActiveState(wantedPersonsGridColumns),
  [Agenda.WANTED_VEHICLES]: addActiveState(wantedVehiclesGridColumns)
};

const useColumnSettings = create<ColumnSettingsStore>()((set, get) => ({
  settingsId: null,
  columns: defaultColumnSettings,
  setColumnSettingsId: id => {
    set({
      settingsId: id
    });
  },
  updateColumns: (agenda, newColumns) => {
    set(state => ({
      columns: {
        ...state.columns,
        [agenda]: newColumns
      }
    }));
  },
  serializeColumnSettings: () => {
    const columns = get().columns;
    const serializable = {};
    for (const key of Object.keys(columns)) {
      serializable[key] = columns[key].map(v => ({ label: v.label, width: v.width, active: v.active }));
    }
    return JSON.stringify(serializable);
  },
  deserializeColumnSettings: str => {
    const deserialized = JSON.parse(str);
    const initialColumns = get().columns;
    const result = {};
    for (const key in initialColumns) {
      if (
        Object.prototype.hasOwnProperty.call(initialColumns, key) &&
        Object.prototype.hasOwnProperty.call(deserialized, key)
      ) {
        result[key] = deserialized[key].map(v => ({
          ...initialColumns[key].find(c => v.label === c.label),
          ...v
        }));
      }
    }
    return result as Record<Agenda, any[]>;
  },
  syncColumnSettings: columns => {
    set({
      columns
    });
  },
  addColumnsToDictionaryAgenda: (dictionaryName, columns) => {
    set(state => ({
      columns: {
        ...state.columns,
        [dictionaryName]: columns
      }
    }));
    return get().columns[dictionaryName];
  }
}));

export default useColumnSettings;



// WEBPACK FOOTER //
// ./src/modules/columnSettings/_columnSettingsStore.ts